import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { ConfirmDropdownItem, isConfirmedRun } from '../../confirmTwice';
import { DropdownActionItem, DropdownActions, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { TextArea } from '../../../../../../widgets/textArea';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import StoreIcon from 'src/shared/material_icons/action/store.svg';
import { ComposerSlot, ComposerSlotGlyph } from '../composerSlots';
import { PropertiesPanelResourceProperty } from '../../scene-properties-panel';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { stringFormat, T } from 'src/shared/helpers';
import { showSuccessNotification } from '../../../../../../widgets/notificationToaster';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { CollapseAllIcon, ExpandAllIcon, SingleBranchIcon } from 'src/components/widgets/icons/scene';
import OptionIcon from 'src/shared/material_icons/image/palette.svg';
import styled from 'styled-components';
import { ComposerActionDraftCard, ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import { carriedFrom, reorderIds, startPointerReorder } from '../../pointerReorder';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import useComposerStore, { ComposerOptionFace } from '../../../configurator-composer/composer/composerStore';
import { ComposerRevealButton } from '../composerReveal';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import ComposerPlatformProductDialog from '../../../configurator-composer/composer/dialogs/composerPlatformProductDialog';
import ComposerActionEditor, { changeComposerActionType, composerActionTypeSections, createComposerAction } from './composerActionEditor';
import { ComposerBlock, ComposerBlockList, ComposerBlockSelectMode, ComposerBlockSubtitle, ComposerBlockTitle, ComposerNewButton } from '../composerBlock';
import { PropertiesPanelNumberField } from '../../scene-properties-panel/propertiesPanelNumberField';
import PriceIcon from 'src/shared/material_icons/device/sell.svg';
import { ComposerPickerEntry, useComposerPicker } from '../composerPickerMenu';
import { playComposerActions, playComposerOption, resetComposerPlay, useComposerPlayed } from '../../../configurator-composer/composer/helpers/composerPlay';
import ResetPlayIcon from 'src/shared/material_icons/av/replay.svg';
import { sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import {
    ComposerPlatformProduct,
    ComposerPlatformVariant,
    createComposerPlatformVariantCode,
    loadComposerPlatformVariants,
    parseComposerPlatformVariantCode
} from '../../../configurator-composer/composer/helpers/composerPlatformPricing';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerPanelAddButton,
    ComposerFieldNote,
    ComposerDetailsStack,
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerIconButton,
    ComposerKindBadge,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSection,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerToolbar,
    ComposerSelectAll,
    ComposerToolbarDivider,
    ComposerToolbarSpacer,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerLinksEditor from './composerLinksEditor';
import { ComposerPriceCard } from './composerPriceCard';
import ComposerRetargetPanel from './composerRetargetPanel';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerEcommercePlatform,
    ComposerLogicalOperator,
    ComposerExtensionFieldNode,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    ComposerPricingNode
} from './composerAttributesModels';
import { ComposerUsedBy } from '../composerUsedBy';

const PriceModeOptions = styled.div`
    display: grid;
    gap: 6px;
`;

const PriceModeLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 10px;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-radius: 7px;
    cursor: pointer;

    input {
        margin-top: 2px;
        accent-color: var(--editor-accent, #a94700);
    }

    span {
        display: grid;
        gap: 2px;
    }

    small {
        color: var(--editor-muted, #55595f);
        font-size: 11px;
    }
`;

const ComposerWarningNote = styled.div`
    padding: 7px 9px;
    border: 1px solid var(--composer-warn-line-strong, #f2c98d);
    border-radius: 6px;
    color: var(--composer-warn-ink, #874b0b);
    background: var(--composer-warn-surface, #fff8ed);
    font-size: 11px;
    line-height: 1.35;
`;

// LE PROPRIETÀ IN PIÙ STANNO NELLE SETTINGS E SI SCRIVONO SUBITO (utente, 2026-10-20): un campo per ogni extension
// field definito in Additional Properties, senza Edit/Save/Cancel. Un campo vuoto toglie la proprietà dall'opzione.
const PropertiesContent: FC<{ selectedOption: ComposerAttributeOptionNode; onUpdate: (option: ComposerAttributeOptionNode) => void }> = ({
    selectedOption,
    onUpdate
}) => {
    const globalFields = useComposerStore((store) => store.extensionFieldDefinitions);
    const fields = selectedOption.extensionFields || [];
    const valueOf = (fieldID: string) => fields.find((field) => String(field.extensionFieldID) === fieldID)?.value ?? '';

    const write = (fieldID: string, value: string) => {
        const extensionFields: ComposerExtensionFieldNode[] = globalFields
            .map((field) => ({ field, value: String(field.fieldID) === fieldID ? value : valueOf(String(field.fieldID)) }))
            .filter(({ value }) => value !== '')
            .map(({ field, value }) => ({
                extensionFieldID: Number(field.fieldID),
                name: field.name || '',
                label: field.label || '',
                value,
                dataTypeID: field.dataTypeID,
                operationTypeID: field.operationTypeID,
                formatString: field.formatString || ''
            }));
        onUpdate({ ...selectedOption, extensionFields });
    };

    if (globalFields.length === 0) {
        return <ComposerUnsupportedNote>{T._('No extension fields are configured in Additional Properties.', 'Admin')}</ComposerUnsupportedNote>;
    }

    return (
        <>
            {globalFields.map((field) => (
                <FormControl key={String(field.fieldID)} label={field.label || field.name || ''}>
                    <Input
                        type='text'
                        placeholder={field.dataTypeName || ''}
                        value={valueOf(String(field.fieldID))}
                        onChange={(event) => write(String(field.fieldID), event.target.value)}
                    />
                </FormControl>
            ))}
        </>
    );
};

// PIÙ PREZZI, CIASCUNO CON LE SUE CONDIZIONI (prototipo, Prices; utente 2026-10-21): la versione vecchia di Zakeke li
// aveva, e il motore e l'API li tengono (`constraintExpression` di ogni prezzo). Una card per prezzo: in testata l'importo
// e quando vale, aperta l'importo e le condizioni. Un prezzo senza condizioni vale sempre, e ce n'è al più uno.
// Per i prodotti legati a uno store (addebito o variante) resta la card unica qui sotto: che lo store sappia applicare
// un prezzo a condizione non è verificato.
const ConditionalPrices: FC<{
    attributes: ComposerAttributeNode[];
    selectedAttribute: ComposerAttributeNode;
    selectedOption: ComposerAttributeOptionNode;
    onUpdateAttribute: (attribute: ComposerAttributeNode) => void;
}> = ({ attributes, selectedAttribute, selectedOption, onUpdateAttribute }) => {
    const all = selectedAttribute.pricings || [];
    const mine = all.map((pricing, index) => ({ pricing, index })).filter(({ pricing }) => pricing.selectedOptionGuid === selectedOption.id);
    const [openKeys, setOpenKeys] = React.useState<number[]>(() => (mine.length === 1 ? [0] : []));
    const toggle = (key: number) => setOpenKeys((current) => (current.includes(key) ? current.filter((candidate) => candidate !== key) : [key]));
    const write = (globalIndex: number, patch: Partial<ComposerPricingNode>) =>
        onUpdateAttribute({ ...selectedAttribute, pricings: all.map((pricing, index) => (index === globalIndex ? { ...pricing, ...patch } : pricing)) });
    const remove = (globalIndex: number, key: number) => {
        onUpdateAttribute({ ...selectedAttribute, pricings: all.filter((_, index) => index !== globalIndex) });
        setOpenKeys((current) => current.filter((candidate) => candidate !== key).map((candidate) => (candidate > key ? candidate - 1 : candidate)));
    };
    const add = () => {
        const pricing: ComposerPricingNode = {
            attributePricingID: 0,
            selectedOptionID: selectedOption.idNumeric || 0,
            selectedOptionGuid: selectedOption.id,
            selectedValue: '',
            price: 0,
            pricingID: 0,
            name: selectedOption.name,
            links: [],
            linkOperation: ComposerLogicalOperator.AND
        };
        onUpdateAttribute({ ...selectedAttribute, pricings: [...all, pricing] });
        setOpenKeys([mine.length]);
    };
    if (mine.length === 0)
        return (
            <ComposerDetailsStack>
                <ComposerFieldNote>{T._('This option costs nothing on top.', 'Admin')}</ComposerFieldNote>
                <NewBlockButton type='button' onClick={add}>
                    <PlusIcon />
                    {T._('New price', 'Admin')}
                </NewBlockButton>
            </ComposerDetailsStack>
        );

    return (
        <ComposerBlockList>
            {mine.map(({ pricing, index }, key) => (
                <ComposerPriceCard
                    key={`${index}_${pricing.attributePricingID}`}
                    id={`price-${selectedOption.id}-${key}`}
                    pricing={pricing}
                    attributes={attributes}
                    excludeAttributeIDs={[selectedAttribute.id]}
                    open={openKeys.includes(key)}
                    onToggleOpen={() => toggle(key)}
                    hasError={(linkIndex) =>
                        !!selectedOption.brokenValues?.has(`${ComposerValidationErrorCode.PRICE_CONDITION_INCOMPLETE}:${key}:${linkIndex}`)
                    }
                    onChange={(patch) => write(index, patch)}
                    onRemove={() => remove(index, key)}
                />
            ))}
            <NewBlockButton type='button' onClick={add}>
                <PlusIcon />
                {T._('New price', 'Admin')}
            </NewBlockButton>
        </ComposerBlockList>
    );
};

const PriceContent: FC<{
    attributes: ComposerAttributeNode[];
    selectedAttribute: ComposerAttributeNode;
    selectedOption: ComposerAttributeOptionNode;
    ecommercePlatform: ComposerEcommercePlatform;
    onUpdateAttribute: (attribute: ComposerAttributeNode) => void;
    onUpdateOption: (option: ComposerAttributeOptionNode) => void;
}> = ({ attributes, selectedAttribute, selectedOption, ecommercePlatform, onUpdateAttribute, onUpdateOption }) => {
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const optionPricing = (selectedAttribute.pricings || []).find((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const [price, setPrice] = React.useState(optionPricing?.price?.toString() || '');
    const [chargeName, setChargeName] = React.useState(optionPricing?.name || selectedOption.name);
    const linkedVariant = React.useMemo(() => parseComposerPlatformVariantCode(selectedOption.code), [selectedOption.code]);
    const isPlatformCharge = ecommercePlatform === ComposerEcommercePlatform.Shopify || ecommercePlatform === ComposerEcommercePlatform.BigCommerce;
    const [adjustPrice, setAdjustPrice] = React.useState(Boolean(optionPricing || linkedVariant));
    const [priceMode, setPriceMode] = React.useState<'charge' | 'variant'>(linkedVariant ? 'variant' : 'charge');
    const [selectedProduct, setSelectedProduct] = React.useState<ComposerPlatformProduct | null>(
        linkedVariant
            ? {
                  code: linkedVariant.product.code,
                  name: linkedVariant.product.code,
                  metadata: linkedVariant.product.metadata
              }
            : null
    );
    const [variants, setVariants] = React.useState<ComposerPlatformVariant[]>(linkedVariant ? [linkedVariant.variant] : []);
    const [variantsLoading, setVariantsLoading] = React.useState(false);
    const [variantsError, setVariantsError] = React.useState<string | null>(null);
    const [priceOpen, setPriceOpen] = React.useState(true);

    React.useEffect(() => {
        const pricing = (selectedAttribute.pricings || []).find((candidate) => candidate.selectedOptionGuid === selectedOption.id);
        setPrice(pricing?.price?.toString() || '');
        setChargeName(pricing?.name || selectedOption.name);
        const currentVariant = parseComposerPlatformVariantCode(selectedOption.code);
        setAdjustPrice(Boolean(pricing || currentVariant));
        setPriceMode(currentVariant ? 'variant' : 'charge');
        setSelectedProduct((currentProduct) =>
            currentVariant
                ? currentProduct?.code === currentVariant.product.code
                    ? currentProduct
                    : { code: currentVariant.product.code, name: currentVariant.product.code, metadata: currentVariant.product.metadata }
                : null
        );
        setVariants((currentVariants) =>
            currentVariant ? (currentVariants.some((variant) => variant.code === currentVariant.variant.code) ? currentVariants : [currentVariant.variant]) : []
        );
        setVariantsError(null);
        // A mode change clears the old price before a variant is selected. Syncing on
        // every intermediate store update would consequently turn Adjust price off.
        // The selected option is the boundary at which this local price UI must reset.
    }, [selectedOption.id]);

    const removeOptionPricing = () => {
        onUpdateAttribute({
            ...selectedAttribute,
            pricings: (selectedAttribute.pricings || []).filter((pricing) => pricing.selectedOptionGuid !== selectedOption.id)
        });
    };

    const upsertOptionPricing = (value: string, name = chargeName) => {
        const currentPricings = [...(selectedAttribute.pricings || [])];
        const index = currentPricings.findIndex((pricing) => pricing.selectedOptionGuid === selectedOption.id);
        const pricing: ComposerPricingNode = {
            attributePricingID: optionPricing?.attributePricingID || 0,
            selectedOptionID: selectedOption.idNumeric || 0,
            selectedOptionGuid: selectedOption.id,
            selectedValue: '',
            price: Number.parseFloat(value) || 0,
            pricingID: optionPricing?.pricingID || 0,
            name: name.trim() || selectedOption.name
        };
        index >= 0 ? (currentPricings[index] = pricing) : currentPricings.push(pricing);
        onUpdateAttribute({ ...selectedAttribute, pricings: currentPricings });
    };

    const updatePrice = (value: string) => {
        setPrice(value);
        upsertOptionPricing(value);
    };

    const selectProduct = async (product: ComposerPlatformProduct) => {
        setSelectedProduct(product);
        setVariants([]);
        setVariantsLoading(true);
        setVariantsError(null);
        try {
            const loadedVariants = await loadComposerPlatformVariants(product.code);
            setVariants(loadedVariants);
            if (loadedVariants.length === 1) {
                onUpdateOption({
                    ...selectedOption,
                    platformAttribute: undefined,
                    code: createComposerPlatformVariantCode(product, loadedVariants[0])
                });
            } else {
                onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
            }
        } catch (error) {
            setVariantsError(T._('Store variants could not be loaded. Choose the product again or retry later.', 'Admin'));
        } finally {
            setVariantsLoading(false);
        }
    };

    const setPlatformPricingEnabled = (enabled: boolean) => {
        setAdjustPrice(enabled);
        setVariantsError(null);
        if (!enabled) {
            removeOptionPricing();
            setSelectedProduct(null);
            setVariants([]);
            onUpdateOption({
                ...selectedOption,
                platformAttribute: undefined,
                code: selectedOption.code.trim() && !linkedVariant ? selectedOption.code : createComposerLocalCode(selectedOption.name, 'option')
            });
            return;
        }

        setPriceMode('charge');
        setPrice(optionPricing?.price?.toString() || '');
        setChargeName(optionPricing?.name || selectedOption.name);
        upsertOptionPricing(optionPricing?.price?.toString() || '', optionPricing?.name || selectedOption.name);
        onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
    };

    const setPlatformPriceMode = (mode: 'charge' | 'variant') => {
        setPriceMode(mode);
        setVariantsError(null);
        if (mode === 'charge') {
            setSelectedProduct(null);
            setVariants([]);
            upsertOptionPricing(price, chargeName);
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        } else {
            removeOptionPricing();
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        }
    };

    // IL PRODOTTO È UNO SLOT COME LA SUA VARIANTE (utente, 2026-10-15): l'elenco dei prodotti dello store è
    // lungo e si cerca sul server, quindi la riga apre la finestra di sempre invece di una tendina.
    const pickProduct = () =>
        showDialog(
            'composerPlatformProduct',
            <ComposerPlatformProductDialog
                salesChannelID={productState.salesChannelID}
                forbiddenProductCode={productState.product.code}
                onClose={() => closeDialog('composerPlatformProduct')}
                onSelect={(product) => void selectProduct(product)}
            />
        );

    if (!isPlatformCharge)
        return (
            <ConditionalPrices
                attributes={attributes}
                selectedAttribute={selectedAttribute}
                selectedOption={selectedOption}
                onUpdateAttribute={onUpdateAttribute}
            />
        );

    // IL PREZZO È UNA CARD (prototipo, Pricing; utente 2026-10-20): una sola, perché il prodotto ne salva una per
    // opzione — più prezzi e prezzi a condizione non sono verificati sul backend. Senza prezzo una riga lo dice, e
    // «New price» lo crea; Remove nel «…» lo toglie.
    const hasPricing = isPlatformCharge ? adjustPrice : !!optionPricing;
    const amount = Number(price) || 0;
    const subtitle =
        isPlatformCharge && priceMode === 'variant'
            ? linkedVariant
                ? stringFormat(T._('Variant of {0}', 'Admin'), linkedVariant.product.code)
                : T._('Choose a store variant', 'Admin')
            : `${amount >= 0 ? '+' : ''}${amount.toFixed(2)}`;
    const createPrice = () => (isPlatformCharge ? setPlatformPricingEnabled(true) : updatePrice('0'));
    const removePrice = () => (isPlatformCharge ? setPlatformPricingEnabled(false) : (setPrice(''), removeOptionPricing()));

    if (!hasPricing) {
        return (
            <ComposerDetailsStack>
                <ComposerFieldNote>{T._('This option costs nothing on top.', 'Admin')}</ComposerFieldNote>
                <NewBlockButton type='button' onClick={createPrice}>
                    <PlusIcon />
                    {T._('New price', 'Admin')}
                </NewBlockButton>
            </ComposerDetailsStack>
        );
    }

    return (
        <ComposerBlockList>
            <ComposerBlock
                id={`price-${selectedOption.id}`}
                open={priceOpen}
                onToggleOpen={() => setPriceOpen(!priceOpen)}
                glyph={<PriceIcon />}
                title={
                    <>
                        <ComposerBlockTitle $open={priceOpen}>{T._('Price', 'Admin')}</ComposerBlockTitle>
                        <ComposerBlockSubtitle>{subtitle}</ComposerBlockSubtitle>
                    </>
                }
                menu={[
                    [
                        <ConfirmDropdownItem
                            key='remove'
                            icon={
                                <DropdownItemIcon>
                                    <DeleteIcon />
                                </DropdownItemIcon>
                            }
                            onConfirm={removePrice}
                        >
                            {T._('Remove', 'Admin')}
                        </ConfirmDropdownItem>
                    ]
                ]}
            >
                {isPlatformCharge ? (
                    <>
                        <PriceModeOptions role='radiogroup' aria-label={T._('Price adjustment type', 'Admin')}>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'charge'} onChange={() => setPlatformPriceMode('charge')} />
                                <span>
                                    <strong>{T._('Create a charge', 'Admin')}</strong>
                                    <small>{T._('Creates an active product that remains hidden in your store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'variant'} onChange={() => setPlatformPriceMode('variant')} />
                                <span>
                                    <strong>{T._('Use a variant from another (existing) product in your shop', 'Admin')}</strong>
                                    <small>{T._('Use the price of a variant that already exists in the store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                        </PriceModeOptions>
                        {priceMode === 'charge' ? (
                            <>
                                <FormControl label={T._('Charge name', 'Admin')}>
                                    <Input
                                        value={chargeName}
                                        placeholder={selectedOption.name}
                                        onChange={(event) => {
                                            setChargeName(event.target.value);
                                            upsertOptionPricing(price, event.target.value);
                                        }}
                                    />
                                </FormControl>
                                <FormControl label={T._('Price', 'Admin')}>
                                    <PropertiesPanelNumberField full value={Number(price) || 0} step={0.01} onChange={(value) => updatePrice(String(value))} />
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <FormControl label={T._('Store product', 'Admin')}>
                                    <div data-composer-wide>
                                        <PropertiesPanelResourceProperty
                                            id={`composer-option-${selectedOption.id}-product`}
                                            preview={
                                                <ComposerSlotGlyph>
                                                    {selectedProduct?.thumbnail ? <img src={selectedProduct.thumbnail} alt='' /> : <StoreIcon />}
                                                </ComposerSlotGlyph>
                                            }
                                            name={selectedProduct?.name}
                                            caption={selectedProduct?.code}
                                            emptyLabel={T._('Choose a product', 'Admin')}
                                            onPick={pickProduct}
                                            onReplace={pickProduct}
                                        />
                                    </div>
                                </FormControl>
                                {selectedProduct && (
                                    <FormControl label={T._('Store variant', 'Admin')}>
                                        <ComposerSlot
                                            id={`composer-option-${selectedOption.id}-variant`}
                                            value={linkedVariant?.variant.code}
                                            options={variants.map((variant) => ({
                                                value: variant.code,
                                                label: variant.name || variant.sku || variant.code,
                                                caption: variant.sku && variant.name ? variant.sku : undefined
                                            }))}
                                            glyph={<StoreIcon />}
                                            caption={T._('Store variant', 'Admin')}
                                            emptyLabel={variantsLoading ? T._('Loading variants…', 'Admin') : T._('Choose a variant', 'Admin')}
                                            searchPlaceholder={T._('Search variants…', 'Admin')}
                                            noOptionsLabel={T._('This product has no variants.', 'Admin')}
                                            disabled={variantsLoading}
                                            clearable
                                            onChange={(code) => {
                                                const variant = variants.find((candidate) => candidate.code === code);
                                                onUpdateOption({
                                                    ...selectedOption,
                                                    platformAttribute: undefined,
                                                    code: variant ? createComposerPlatformVariantCode(selectedProduct, variant) : ''
                                                });
                                            }}
                                        />
                                    </FormControl>
                                )}
                                {variantsError && <ComposerWarningNote>{variantsError}</ComposerWarningNote>}
                            </>
                        )}
                    </>
                ) : (
                    <FormControl label={T._('Amount', 'Admin')} tooltipLabelHtml={T._('Added to the product price when this option is chosen.', 'Admin')}>
                        <PropertiesPanelNumberField full value={amount} step={0.01} onChange={(value) => updatePrice(String(value))} />
                    </FormControl>
                )}
            </ComposerBlock>
            <ComposerFieldNote>{T._('One price per option: the product saves one price adjustment for each option.', 'Admin')}</ComposerFieldNote>
        </ComposerBlockList>
    );
};

const NewBlockButton = styled(ComposerNewButton)`
    margin-top: 10px;
`;

// RESET ACCANTO AL PLAY (utente, 2026-10-21; prima era accanto ad Add nell'albero): compare dopo un Play e riporta il
// modello com'era. Tondo come il Play ma bianco: torna indietro, non è l'azione principale.
const HeadReset = styled.button`
    display: grid;
    flex: none;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-radius: 50%;
    place-items: center;
    background: var(--editor-panel, #ffffff);
    color: var(--editor-muted, #55595f);
    cursor: pointer;

    &:hover {
        background: var(--editor-sunken, #e9ebed);
        color: var(--editor-fg, #262a30);
    }

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

// Il Play dell'opzione accanto al nome: tondo, nell'arancio morbido dell'azione principale.
const HeadPlay = styled.button`
    display: grid;
    flex: none;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid var(--editor-accent-line, #e2b597);
    border-radius: 50%;
    place-items: center;
    background: var(--editor-accent-wash, #f6efe9);
    color: var(--editor-accent, #a94700);
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--editor-accent-wash-hover, #f1e4d9);
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

const ComposerActions: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    actions: ComposerOptionActionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerOptionActionNode>;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}> = ({ selectedAttribute, selectedOption, actions, multiple, state, materials = [], meshes = [], cameras = [] }) => {
    const { state: productState } = useProductConfigurationState();
    const [sceneEditor] = usePreview3DStore((store) => [store.sceneEditor]);
    const played = useComposerPlayed((store) => store.played);
    const [
        attributes,
        updateAction,
        removeAction,
        updateOption,
        updateAttribute,
        selectActions,
        ecommerceSettings,
        optionFace,
        setOptionFace,
        validateOptions,
        merchantProductOptions,
        printTypes
    ] = useComposerStore((store) => [
        store.attributes,
        store.updateAction,
        store.removeAction,
        store.updateOption,
        store.updateAttribute,
        store.selectActions,
        store.ecommerceSettings,
        store.optionFace,
        store.setOptionFace,
        store.validateOptions,
        store.merchantProductOptions,
        store.printTypes
    ]);
    const cloneAction = useComposerStore((store) => store.cloneAction);
    const selectionMenuRef = React.useRef<HTMLButtonElement>(null);
    const { showDialog, closeDialog } = useDialogManager();
    const { selectedIds, filteredData, addNode, updateNode, findNodeById, removeNodes, setSelectedIds } = state;

    // APERTA E SCELTA SONO DUE COSE (utente, 2026-10-15; prototipo, Rules): una card aperta mostra il suo
    // editor, una card scelta porta il filo arancio ed entra nel mucchio su cui agisce il cestino. Prima
    // erano la stessa: si apriva soltanto la card scelta da sola, e scegliere più card le chiudeva tutte.
    // «One card at a time» acceso — com'era prima, quindi di partenza — aprirne una chiude le altre.
    const [openIds, setOpenIds] = React.useState<string[]>([]);
    const [singleCard, setSingleCard] = React.useState(true);
    // LA CARD CHE NASCE NON È ANCORA UN'AZIONE: sta qui finché non ha un tipo, e solo allora entra nello store.
    const [drafting, setDrafting] = React.useState(false);

    React.useEffect(() => {
        setSelectedIds([]);
        selectActions(null);
        setOpenIds([]);
        setDrafting(false);
    }, [selectedOption?.id]);

    const openCard = (id: string) => setOpenIds((current) => (singleCard ? [id] : current.includes(id) ? current : [...current, id]));
    const closeCard = (id: string) => setOpenIds((current) => current.filter((candidate) => candidate !== id));
    const clearSelection = () => {
        setSelectedIds([]);
        selectActions(null);
    };

    const addAction = () => {
        if (!selectedOption) return;
        setDrafting(true);
        setOptionFace('actions');
    };

    const createAction = (type: ComposerOptionActionsType) => {
        if (!selectedOption) return;
        const action = createComposerAction(selectedAttribute.id, selectedOption.id, type, actions.length);
        addNode(action);
        updateAction(action);
        setDrafting(false);
        setSelectedIds([action.id]);
        selectActions([action]);
        openCard(action.id);
    };

    // CLONE NEL «…» DELLA SELEZIONE: le copie nascono in fondo e restano scelte, così si vede cosa è nato.
    const cloneActions = (actionsToClone: ComposerOptionActionNode[]) => {
        const clones = actionsToClone.map((action) => {
            const clone = cloneAction(action);
            addNode(clone);
            return clone;
        });
        setSelectedIds(clones.map((clone) => clone.id));
        selectActions(clones);
    };

    const selectedActionNodes = () => selectedIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[];

    const playAction = (action: ComposerOptionActionNode) => playComposerActions(sceneEditor, selectedAttribute.idNumeric, [action], false);

    const removeActions = (actionsToRemove: ComposerOptionActionNode[]) => {
        if (actionsToRemove.length === 0) return;
        const run = () => {
            const ids = actionsToRemove.map((action) => action.id);
            removeNodes(ids);
            actionsToRemove.forEach((action) => removeAction(action.attributeID, action.optionID, action.id));
            setOpenIds((current) => current.filter((id) => !ids.includes(id)));
            setSelectedIds([]);
            selectActions(null);
        };
        // Dal cestino si chiede due volte, senza finestra (confirmTwice): arrivati qui, la domanda è già fatta.
        if (isConfirmedRun()) return run();
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected action(s)?', 'Admin')}
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            run();
                        }
                    }
                ]}
            />
        );
    };

    // SCEGLIERE È LA CASELLA, O ⇧ / ⌘ SULLA TESTATA (prototipo, utente 2026-10-20); il clic semplice apre e chiude.
    const selectAction = (mode: ComposerBlockSelectMode, action: ComposerOptionActionNode) => {
        setOptionFace('actions');
        if (mode === 'range' && selectedIds.length > 0) {
            const visibleActions = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
            const start = visibleActions.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = visibleActions.findIndex((candidate) => candidate.id === action.id);
            if (start < 0 || end < 0) return;
            const range = visibleActions.slice(Math.min(start, end), Math.max(start, end) + 1);
            setSelectedIds(range.map((candidate) => candidate.id));
            selectActions(range);
            return;
        }
        const nextIds = selectedIds.includes(action.id) ? selectedIds.filter((id) => id !== action.id) : [...selectedIds, action.id];
        setSelectedIds(nextIds);
        selectActions(nextIds.length ? (nextIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[]) : null);
    };
    const toggleCard = (id: string) => (openIds.includes(id) ? closeCard(id) : openCard(id));

    // COPY TO: le azioni scelte, copiate in fondo a un'altra opzione (prototipo, utente 2026-10-20). Ogni copia ha il
    // suo id e appartiene all'opzione di arrivo; l'originale resta dov'è.
    const { openPicker, picker: copyPicker } = useComposerPicker();
    const copyActionsTo = (actionsToCopy: ComposerOptionActionNode[], target: ComposerAttributeOptionNode) => {
        const start = Math.max(-1, ...target.actions.map((candidate) => candidate.displayOrder)) + 1;
        actionsToCopy.forEach((action, index) =>
            updateAction({
                ...action,
                id: Zakeke.generateUUID(),
                attributeID: target.attributeID,
                optionID: target.id,
                displayOrder: start + index,
                isNew: true
            })
        );
        showSuccessNotification({
            message: stringFormat(
                actionsToCopy.length === 1 ? T._('Action copied to “{0}”.', 'Admin') : T._('{0} actions copied to “{1}”.', 'Admin'),
                ...(actionsToCopy.length === 1 ? [target.name] : [actionsToCopy.length.toString(), target.name])
            )
        });
    };
    const openActionCopyPicker = (anchor: HTMLElement | null | undefined, actionsToCopy: ComposerOptionActionNode[]) => {
        const entries: ComposerPickerEntry[] = useComposerStore
            .getState()
            .attributes.filter((attribute) => attribute.options.length > 0)
            .flatMap((attribute) =>
                attribute.options
                    .filter((option) => option.id !== selectedOption?.id)
                    .map((option, index) => ({
                        id: option.id,
                        label: option.name,
                        section: index === 0 ? attribute.name : undefined,
                        onPick: () => copyActionsTo(actionsToCopy, option)
                    }))
            );
        openPicker(anchor, entries, T._('Copy to', 'Admin'));
    };

    // IL TIPO SI CAMBIA DAL TITOLO: restano gli input che il nuovo tipo sa usare. LA CARD SI APRE (utente, 2026-10-21):
    // cambiato il tipo, c'è di sicuro qualcosa da scegliere.
    const changeActionType = (action: ComposerOptionActionNode, type: ComposerOptionActionsType) => {
        if (action.type === type) return;
        changeAction(changeComposerActionType(action, type));
        openCard(action.id);
    };

    // OGNI RIGA DELLA CARD SCRIVE SUBITO (utente, 2026-10-15): niente Save. Lo store rivalida l'azione, e la
    // card riprende la versione rivalidata — con i suoi errori — come fa l'occhio.
    const changeAction = (nextAction: ComposerOptionActionNode) => {
        updateAction(nextAction);
        const validatedAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === nextAction.attributeID)
                ?.options.find((option) => option.id === nextAction.optionID)
                ?.actions.find((candidate) => candidate.id === nextAction.id) ?? nextAction;
        updateNode(validatedAction.id, validatedAction);
    };

    const moveAction = (action: ComposerOptionActionNode, direction: 'up' | 'down') => {
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const index = siblings.findIndex((candidate) => candidate.id === action.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= siblings.length) return;
        // SI RINUMERA 0..n COME COL TRASCINAMENTO, non si scambiano i due displayOrder: dopo una rimozione e un'aggiunta
        // due card possono avere lo stesso numero (la nuova prende actions.length), e scambiare due numeri uguali non
        // spostava niente.
        [siblings[index], siblings[swapIndex]] = [siblings[swapIndex], siblings[index]];
        siblings.forEach((candidate, displayOrder) => {
            if (candidate.displayOrder === displayOrder) return;
            updateNode(candidate.id, { ...candidate, displayOrder });
            updateAction({ ...candidate, displayOrder });
        });
    };

    // IL TRASCINAMENTO DELLE CARD (pointerReorder; utente, 2026-10-15): si sposta la card intera, e una
    // card scelta porta con sé tutte le card scelte, nel loro ordine.
    const startActionDrag = (event: React.PointerEvent<HTMLButtonElement>, dragged: ComposerOptionActionNode) => {
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const ordered = siblings.map((action) => action.id);
        const carriedIds = carriedFrom(ordered, dragged.id, selectedIds);
        startPointerReorder(event, {
            draggedId: dragged.id,
            carriedIds,
            canDrop: (targetId, position) => position !== 'into' && ordered.includes(targetId) && !carriedIds.includes(targetId),
            onDrop: (targetId, position) => {
                if (position === 'into') return;
                reorderIds(ordered, carriedIds, targetId, position).forEach((id, displayOrder) => {
                    const action = siblings.find((candidate) => candidate.id === id);
                    if (!action || action.displayOrder === displayOrder) return;
                    updateNode(action.id, { ...action, displayOrder });
                    updateAction({ ...action, displayOrder });
                });
            }
        });
    };

    const toggleActionEnabled = (action: ComposerOptionActionNode) => {
        const currentAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === action.attributeID)
                ?.options.find((option) => option.id === action.optionID)
                ?.actions.find((candidate) => candidate.id === action.id) ?? action;
        const updatedAction = { ...currentAction, enabled: currentAction.enabled === false };
        updateAction(updatedAction);
        const validatedAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === updatedAction.attributeID)
                ?.options.find((option) => option.id === updatedAction.optionID)
                ?.actions.find((candidate) => candidate.id === updatedAction.id) ?? updatedAction;
        updateNode(validatedAction.id, validatedAction);
    };

    if (!selectedOption || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState icon={<OptionIcon />}>{T._('Select one option to see what it does.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const nodes = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
    // LE TAB DEL PROTOTIPO, COI NOSTRI NOMI (utente, 2026-10-20): Actions, Links, Prices, Retarget, Settings. Properties
    // è una section delle Settings.
    const OPTION_FACES: ComposerOptionFace[] = ['actions', 'links', 'prices', 'retarget', 'settings'];
    const activeTab = Math.max(0, OPTION_FACES.indexOf(optionFace));
    // Quanti prezzi ha l'opzione: con le condizioni possono essere più d'uno; la variante di uno store conta per uno.
    const priceCount = Math.max(
        selectedAttribute.pricings?.filter((pricing) => pricing.selectedOptionGuid === selectedOption.id).length ?? 0,
        parseComposerPlatformVariantCode(selectedOption.code) ? 1 : 0
    );
    const priceSupported = [ComposerEcommercePlatform.WooCommerce, ComposerEcommercePlatform.Shopify, ComposerEcommercePlatform.BigCommerce].includes(
        ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None
    );
    const updateSelectedOption = (patch: Partial<ComposerAttributeOptionNode>) => {
        const updatedOption = { ...selectedOption, ...patch };
        updateOption(updatedOption);
        if (
            patch.name !== undefined ||
            patch.code !== undefined ||
            patch.links !== undefined ||
            patch.linkOperation !== undefined ||
            Object.prototype.hasOwnProperty.call(patch, 'platformAttribute')
        ) {
            void validateOptions({
                ...selectedAttribute,
                options: selectedAttribute.options.map((option) => (option.id === updatedOption.id ? updatedOption : option))
            });
        }
    };
    const updateSelectedOptionImage = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        updateOption(applyComposerOptionImage(selectedOption, image));
        return image?.imageUrl ?? '';
    };
    // A local option priced on its own is exempt from the code requirement, so its empty field must not read as broken.
    const hasCustomPricing = (selectedAttribute.pricings || []).some((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const isStoreConnected = Boolean(selectedAttribute.platformAttribute?.id);
    const isStoreOption = Boolean(selectedOption.platformAttribute?.id) || isComposerStoreCode(selectedOption.code);
    const storeOptionValues = isStoreConnected
        ? (merchantProductOptions.find((attribute) => attribute.optionID === selectedAttribute.platformAttribute?.id)?.values ?? [])
        : [];
    const storeOptions = [
        { value: '', label: T._('Select a store option', 'Admin') },
        ...storeOptionValues.map((option) => ({ value: option.valueID, label: option.label || option.valueID }))
    ];
    const optionNameHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NAME_REQUIRED,
        ComposerValidationErrorCode.OPTION_NAME_NOT_UNIQUE
    );
    const optionCodeHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_CODE_REQUIRED,
        ComposerValidationErrorCode.OPTION_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const optionStoreHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NOT_FROM_PLATFORM,
        ComposerValidationErrorCode.OPTION_FROM_PLATFORM_WITH_LOCAL_ATTRIBUTE,
        ComposerValidationErrorCode.STORE_OPTION_NOT_FOUND,
        ComposerValidationErrorCode.STORE_OPTION_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );

    return (
        <ComposerPanel aria-label={T._('Option details', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='option'>{selectedOption.img ? <img src={selectedOption.img} alt='' /> : <OptionIcon />}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Option name', 'Admin')}
                        placeholder={T._('Enter option name', 'Admin')}
                        isError={optionNameHasError}
                        value={selectedOption.name}
                        onChange={(event) => updateSelectedOption({ name: event.target.value })}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='option'>{T._('Option', 'Admin')}</ComposerKindBadge>
                        {isStoreOption ? (
                            <ComposerKindBadge $kind='attribute'>{T._('Store attribute', 'Admin')}</ComposerKindBadge>
                        ) : (
                            <span>{selectedOption.code}</span>
                        )}
                        <ComposerUsedBy subjectID={selectedOption.id} kind='option' />
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                {/* PLAY IN TESTATA (prototipo, utente 2026-10-20): mostra l'opzione sul modello, con tutte le sue azioni accese. */}
                <ZTooltip title={T._('Play this option on the model', 'Admin')}>
                    <HeadPlay
                        type='button'
                        aria-label={T._('Play this option on the model', 'Admin')}
                        disabled={!actions.some((action) => action.enabled !== false)}
                        onClick={() => playComposerOption(sceneEditor, selectedOption)}
                    >
                        <PlayIcon />
                    </HeadPlay>
                </ZTooltip>
                {played && (
                    <ZTooltip title={T._('Reset the model to the default options', 'Admin')}>
                        <HeadReset type='button' aria-label={T._('Reset the model', 'Admin')} onClick={() => void resetComposerPlay(sceneEditor)}>
                            <ResetPlayIcon />
                        </HeadReset>
                    </ZTooltip>
                )}
                <ComposerRevealButton id={selectedOption.id} />
            </ComposerSubjectHeader>
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setOptionFace(OPTION_FACES[index] ?? 'actions')}>
                <TabList hasOverflow>
                    <Tab>
                        {T._('Actions', 'Admin')} <ComposerTabCount>{actions.length}</ComposerTabCount>
                    </Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedOption.links.length}</ComposerTabCount>
                    </Tab>
                    <Tab>
                        {T._('Prices', 'Admin')} <ComposerTabCount>{priceCount}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Retarget', 'Admin')}</Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerToolbar>
                        {/* ADD APRE SUBITO IL MENU DEI TIPI, A FAMIGLIE (prototipo): l'azione nasce già col suo tipo. */}
                        <DropdownActions
                            header={
                                <ComposerPanelAddButton type='button'>
                                    <PlusIcon />
                                    {T._('Add', 'Admin')}
                                </ComposerPanelAddButton>
                            }
                            sections={composerActionTypeSections(createAction)}
                            minWidth='236px'
                            orientation='LEFT'
                        />
                        <ComposerToolbarSpacer />
                        {/* LA TESTATA È UGUALE IN OGNI ELENCO (prototipo, AGGIORNAMENTI §4): Add a sinistra; a destra Select all,
                            un divisore, i comandi dell'elenco e in fondo il «…» della selezione. Senza card non cambia forma:
                            i comandi ci sono, spenti. */}
                        <ComposerSelectAll
                            state={selectedIds.length === 0 ? 'none' : nodes.every((action) => selectedIds.includes(action.id)) ? 'all' : 'some'}
                            disabled={nodes.length === 0}
                            onToggle={() => {
                                const all = nodes.length > 0 && nodes.every((action) => selectedIds.includes(action.id));
                                setSelectedIds(all ? [] : nodes.map((action) => action.id));
                                selectActions(all ? null : nodes);
                            }}
                        />
                        <ComposerToolbarDivider aria-hidden='true' />
                        <ZTooltip
                            title={singleCard ? T._('One card at a time: opening a card closes the others', 'Admin') : T._('One card at a time', 'Admin')}
                        >
                            <ComposerIconButton
                                aria-label={T._('One card at a time', 'Admin')}
                                aria-pressed={singleCard}
                                $active={singleCard}
                                onClick={() => {
                                    if (!singleCard) setOpenIds([]);
                                    setSingleCard(!singleCard);
                                }}
                            >
                                <SingleBranchIcon />
                            </ComposerIconButton>
                        </ZTooltip>
                        <ZTooltip
                            title={
                                singleCard && openIds.length === 0
                                    ? T._('One card at a time is on: open a single card from its head', 'Admin')
                                    : openIds.length > 0
                                      ? T._('Collapse all', 'Admin')
                                      : T._('Expand all', 'Admin')
                            }
                        >
                            <ComposerIconButton
                                aria-label={openIds.length > 0 ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin')}
                                disabled={nodes.length === 0 || (singleCard && openIds.length === 0)}
                                onClick={() => setOpenIds(openIds.length > 0 ? [] : nodes.map((action) => action.id))}
                            >
                                {openIds.length > 0 ? <CollapseAllIcon /> : <ExpandAllIcon />}
                            </ComposerIconButton>
                        </ZTooltip>
                        {selectedIds.length > 0 ? (
                            <DropdownActions
                                header={
                                    <ZTooltip title={T._('Selection commands', 'Admin')} position='bottom'>
                                        <ComposerIconButton ref={selectionMenuRef} aria-label={T._('Selection commands', 'Admin')}>
                                            <MoreIcon />
                                        </ComposerIconButton>
                                    </ZTooltip>
                                }
                                sections={[
                                    [
                                        <DropdownActionItem key='duplicate' icon={<DuplicateIcon />} onClick={() => cloneActions(selectedActionNodes())}>
                                            {T._('Clone', 'Admin')}
                                        </DropdownActionItem>,
                                        <DropdownActionItem
                                            key='copy'
                                            icon={<CopyToIcon />}
                                            onClick={() => openActionCopyPicker(selectionMenuRef.current, selectedActionNodes())}
                                        >
                                            {T._('Copy to…', 'Admin')}
                                        </DropdownActionItem>
                                    ],
                                    [
                                        <ConfirmDropdownItem
                                            key='remove'
                                            icon={
                                                <DropdownItemIcon>
                                                    <DeleteIcon />
                                                </DropdownItemIcon>
                                            }
                                            onConfirm={() => removeActions(selectedActionNodes())}
                                        >
                                            {T._('Remove', 'Admin')}
                                        </ConfirmDropdownItem>
                                    ]
                                ]}
                                minWidth='236px'
                                orientation='RIGHT'
                            />
                        ) : (
                            <ZTooltip title={T._('Select something in the list first', 'Admin')} position='bottom' addContainer>
                                <ComposerIconButton disabled aria-label={T._('Selection commands', 'Admin')}>
                                    <MoreIcon />
                                </ComposerIconButton>
                            </ZTooltip>
                        )}
                    </ComposerToolbar>
                    <ComposerScrollBody>
                        {/* LE CARD, POI «NEW ACTION» A TUTTA LARGHEZZA IN FONDO (prototipo, New rule): crea una card vuota e
                            aperta, il tipo si sceglie dal suo titolo. A lista vuota, sopra, una riga dice che l'opzione non fa niente. */}
                        {nodes.length === 0 && !drafting && <ComposerFieldNote>{T._('Choosing this option changes nothing yet.', 'Admin')}</ComposerFieldNote>}
                        <ComposerBlockList
                            data-reorder-scope=''
                            onKeyDown={(event) => {
                                // Esc toglie la scelta, non chiude le card: per chiuderle c'è la loro testata.
                                if (event.key !== 'Escape' || selectedIds.length === 0) return;
                                event.stopPropagation();
                                clearSelection();
                            }}
                        >
                            {nodes.map((action) => (
                                <ComposerActionTreeElement
                                    key={action.id}
                                    action={action}
                                    open={openIds.includes(action.id)}
                                    onToggleOpen={() => toggleCard(action.id)}
                                    selected={selectedIds.includes(action.id)}
                                    onSelect={selectAction}
                                    onTypeChange={changeActionType}
                                    findMesh={(id) => meshes.find((mesh) => mesh.id === id || mesh.name === id)}
                                    findMaterial={(id) => materials.find((material) => material.id === id || material.name === id)}
                                    findCamera={(id) => cameras.find((camera) => camera.id === id || camera.name === id)}
                                    findPrintingMethod={(id) => {
                                        const method = printTypes.find((candidate) => String(candidate.printTypeID) === id);
                                        return method ? { name: method.name || id } : undefined;
                                    }}
                                    onGripPointerDown={startActionDrag}
                                    onMoveDown={() => moveAction(action, 'down')}
                                    onMoveUp={() => moveAction(action, 'up')}
                                    onPlay={playAction}
                                    onVisibilityClick={toggleActionEnabled}
                                    onClone={(cloned) => cloneActions([cloned])}
                                    onCopyTo={(copied, anchor) => openActionCopyPicker(anchor, [copied])}
                                    onRemove={(removed) => removeActions([removed])}
                                    editor={
                                        <ComposerActionEditor
                                            key={action.id}
                                            action={action}
                                            materials={materials}
                                            meshes={meshes}
                                            cameras={cameras}
                                            onChange={changeAction}
                                        />
                                    }
                                />
                            ))}
                            {drafting && <ComposerActionDraftCard onChoose={createAction} onDiscard={() => setDrafting(false)} />}
                        </ComposerBlockList>
                        {!drafting && (
                            <NewBlockButton type='button' onClick={addAction}>
                                <PlusIcon />
                                {T._('New action', 'Admin')}
                            </NewBlockButton>
                        )}
                        {copyPicker}
                    </ComposerScrollBody>
                </TabPanel>
                <TabPanel>
                    <ComposerLinksEditor
                        attributes={attributes}
                        links={selectedOption.links}
                        logicalOperator={selectedOption.linkOperation}
                        selfAttributeID={selectedAttribute.id}
                        subjectLabel={T._('This option', 'Admin')}
                        validationErrors={selectedOption.brokenValues}
                        onChange={(links, linkOperation) => updateSelectedOption({ links, linkOperation })}
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        {/* LO STESSO RESPIRO DELLE SETTINGS (prototipo): la card del prezzo sta sull'Inspector, senza section intorno. */}
                        <div>
                            {priceSupported ? (
                                <PriceContent
                                    attributes={attributes}
                                    selectedAttribute={selectedAttribute}
                                    selectedOption={selectedOption}
                                    ecommercePlatform={ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None}
                                    onUpdateAttribute={updateAttribute}
                                    onUpdateOption={updateSelectedOption}
                                />
                            ) : (
                                <ComposerUnsupportedNote>
                                    {T._('Option pricing is available for WooCommerce, Shopify, and BigCommerce.', 'Admin')}
                                </ComposerUnsupportedNote>
                            )}
                        </div>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerRetargetPanel
                        actions={actions}
                        objects={meshes}
                        materials={materials}
                        subjectLabel={selectedOption.name}
                        onApply={(remapped) => {
                            remapped.forEach((action) => updateNode(action.id, action));
                            updateSelectedOption({ actions: remapped });
                        }}
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerSection id='option.general' forceOpen={optionCodeHasError || optionStoreHasError} title={T._('General', 'Admin')}>
                                <FormControl label={T._('Code', 'Admin')}>
                                    {isStoreConnected ? (
                                        <ComposerDetailValue>
                                            {selectedOption.platformAttribute
                                                ? selectedOption.platformAttribute.label || selectedOption.platformAttribute.id
                                                : T._('Choose one of the connected store options below.', 'Admin')}
                                        </ComposerDetailValue>
                                    ) : (
                                        <Input
                                            placeholder={T._('Enter option code', 'Admin')}
                                            isError={optionCodeHasError && !hasCustomPricing}
                                            value={selectedOption.code}
                                            onChange={(event) => updateSelectedOption({ code: event.target.value })}
                                        />
                                    )}
                                </FormControl>
                                <FormControl label={T._('Description', 'Admin')}>
                                    <TextArea
                                        rows={3}
                                        placeholder={T._('Enter option description', 'Admin')}
                                        value={selectedOption.description}
                                        onChange={(event) => updateSelectedOption({ description: event.target.value })}
                                    />
                                </FormControl>
                                {isStoreConnected && (
                                    <FormControl label={T._('Store option', 'Admin')}>
                                        <ComposerSlot
                                            id={`composer-option-${selectedOption.id}-store`}
                                            value={selectedOption.platformAttribute?.id || undefined}
                                            options={storeOptions.filter((option) => option.value)}
                                            glyph={<StoreIcon />}
                                            caption={T._('Store option', 'Admin')}
                                            emptyLabel={T._('Choose a store option', 'Admin')}
                                            searchPlaceholder={T._('Search store options…', 'Admin')}
                                            noOptionsLabel={T._(
                                                'Store options are not available. Create them in the store before connecting this option.',
                                                'Admin'
                                            )}
                                            missingLabel={selectedOption.platformAttribute?.label}
                                            isError={optionStoreHasError}
                                            clearable
                                            onChange={(value) => {
                                                const selection = storeOptions.find((option) => option.value === value && value);
                                                const platformAttribute = selection ? { id: selection.value, label: selection.label } : undefined;
                                                updateSelectedOption({
                                                    platformAttribute,
                                                    code: platformAttribute
                                                        ? JSON.stringify({ ...platformAttribute, selected: true })
                                                        : createComposerLocalCode(selectedOption.name, 'option')
                                                });
                                            }}
                                        />
                                    </FormControl>
                                )}
                            </ComposerSection>
                            <ComposerSection id='option.properties' title={T._('Properties', 'Admin')}>
                                <PropertiesContent selectedOption={selectedOption} onUpdate={updateOption} />
                            </ComposerSection>
                            <ComposerSection id='option.preview-image' title={T._('Preview image', 'Admin')}>
                                <ComposerUploadComponent passedImgUrl={selectedOption.img} onImagePick={updateSelectedOptionImage} />
                                <Composer3DPreviewCapture
                                    materials={materials}
                                    onImagePick={updateSelectedOptionImage}
                                    successMessage={T._('The 3D preview was assigned to this option.', 'Admin')}
                                />
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerActions;
